import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Flex, Form, Spin } from "antd";

import { useAppDispatch } from "../../../../../../../../store/config";
import { createWorkoutStart, updateWorkoutStart } from "../../../../../../../../store/features/workout/workout-slice";
import { ICreateWorkoutPayload, IUpdateWorkoutPayload, IWorkout } from "../../../../../../../../interfaces/store/workout-interface";
import { IUploadFileParams } from "../../../../../../../../interfaces/files/files";
import { AbsoluteButton55, RelativeFlex, StyledColumnWrapper, StyledForm, StyledLayout } from "../../../../../../../../components/common/styles";
import { IMAGE_BASE_URL } from "../../../../../../../../common/config";
import { DropdownField, ImageInputField, TagsDropdownField, TextAreaField, TextInputField } from "../../../../../../../../components/inputs";
import { useDifficultyOptions, useEquipmentTypeOptions, useFilterTags, useInterestsTags } from "../../../../../../useSelectOptions";
import { blobToBase64 } from "../../../../../../../../utils/utils";

const SingleWorkoutDetails = ({
  isEditable,
  setIsEditable,
  workout,
  loading,
  isNew,
  type,
}: {
  isEditable: boolean;
  setIsEditable: (val: boolean) => void;
  workout: IWorkout | null;
  loading: boolean;
  isNew: boolean;
  type: "video" | "exercises";
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);
  const [largeImageData, setLargeImageData] = useState<IUploadFileParams | null>(null);

  const { id: trainerId } = useParams<{ id: string }>();
  const { workoutId } = useParams<{ workoutId: string }>();

  useEffect(() => {
    if (workout) {
      const durationValue = workout.duration ? workout.duration.toString() : workout.minDuration && workout.maxDuration ? `${workout.minDuration}-${workout.maxDuration}` : "";

      form.setFieldsValue({
        name: workout.name,
        trainer: workout.trainer,
        description: workout.description,
        difficulty: workout.difficulty,
        duration: durationValue,
        trainingType: workout.trainingType,
        tags: workout.tags,
        equipmentType: workout.equipmentType,
      });
    }
  }, [workout]);

  const upsertWorkout = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    let duration = null;
    let minDuration = null;
    let maxDuration = null;

    if (values.duration.includes("-")) {
      const [min, max] = values.duration.split("-").map(Number);
      minDuration = min;
      maxDuration = max;
    } else {
      duration = Number(values.duration);
    }

    const workout = {
      name: values.name,
      description: values.description,
      difficulty: values.difficulty,
      duration,
      minDuration,
      maxDuration,
      trainingType: values.trainingType,
      tags: values.tags || [],
      equipmentType: values.equipmentType,
      trainer: trainerId || "",
    };

    const imageDataPayload = imageData
      ? {
          ...imageData,
          binaryFile: await blobToBase64(imageData.binaryFile as Blob),
          fileType: "image",
        }
      : null;

    const largeImageDataPayload = largeImageData
      ? {
          ...largeImageData,
          binaryFile: await blobToBase64(largeImageData.binaryFile as Blob),
          fileType: "image",
        }
      : null;

    const upsertWorkoutBody = {
      workout: { ...workout, ...(isNew ? { type } : {}) },
      ...(workoutId && { workoutId }),
      ...(imageDataPayload && { imageData: imageDataPayload }),
      ...(largeImageDataPayload && { largeImageData: largeImageDataPayload }),
    };

    dispatch(isNew ? createWorkoutStart(upsertWorkoutBody as ICreateWorkoutPayload) : updateWorkoutStart(upsertWorkoutBody as IUpdateWorkoutPayload));
  };

  const filterTags = useFilterTags();
  const interestsTags = useInterestsTags();
  const equipmentTypeOptions = useEquipmentTypeOptions();
  const difficultyOptions = useDifficultyOptions();

  return (
    <>
      <RelativeFlex>
        <AbsoluteButton55 onClick={() => form.submit()}>{isEditable ? t("Save") : t("Edit")}</AbsoluteButton55>
      </RelativeFlex>

      <Spin spinning={loading}>
        <StyledForm form={form} onFinish={upsertWorkout} validateTrigger="onSubmit">
          <StyledLayout>
            <StyledColumnWrapper>
              <TextInputField name="name" label={t("Workout Name")} isEditable={isEditable} rules={[{ required: true, message: t("Required") }]} />
              <Flex gap={20}>
                <>
                  <DropdownField
                    label={t("Workout Type")}
                    options={interestsTags}
                    value={workout?.trainingType}
                    isEditable={isEditable}
                    name="trainingType"
                    rules={[{ required: true, message: t("Required") }]}
                  />
                  <TextInputField
                    name="duration"
                    label={t("Workout Duration in Minutes")}
                    isEditable={isEditable}
                    rules={[
                      { required: true, message: t("Required") },
                      {
                        pattern: /^(\d+(-\d+)?)$/,
                        message: t("Invalid duraion"),
                      },
                    ]}
                  />
                  <DropdownField
                    label={t("Equipment Type")}
                    options={equipmentTypeOptions}
                    value={workout?.equipmentType}
                    isEditable={isEditable}
                    name="equipmentType"
                    rules={[{ required: true, message: t("Required") }]}
                  />
                </>
              </Flex>

              <>
                <Flex gap={20} style={{ width: "50%" }}>
                  <DropdownField label={t("Difficulty")} options={difficultyOptions} isEditable={isEditable} name="difficulty" rules={[{ required: true, message: t("Required") }]} />
                </Flex>
                {!isEditable && <TextAreaField name="description" label={t("Description")} isEditable={isEditable} />}
                <TagsDropdownField mode="multiple" label={t("Tags")} options={filterTags} name="tags" isEditable={isEditable} />
              </>
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Workout Outer Cover Image")}
                isEditable={isEditable}
                value={workout?.outerCover ? IMAGE_BASE_URL + "/" + workout?.outerCover : undefined}
                onChange={setImageData}
                aspect={147 / 187}
              />
              <ImageInputField
                label={t("Workout Inner Cover Image")}
                isEditable={isEditable}
                value={workout?.innerCover ? IMAGE_BASE_URL + "/" + workout.innerCover : undefined}
                onChange={setLargeImageData}
                aspect={390 / 253}
              />
              {isEditable && <TextAreaField name="description" label={t("Description")} isEditable={isEditable} />}
            </StyledColumnWrapper>
          </StyledLayout>
        </StyledForm>
      </Spin>
    </>
  );
};

export default SingleWorkoutDetails;
